/**
 * GlobalBulkOperationQueue
 *
 * This component renders the BulkOperationQueue globally based on API data.
 * It should be placed at a high level in the app (e.g., in PrivateRoutes)
 * and will automatically show/hide based on active bulk operations.
 *
 * Usage:
 * ```tsx
 * import { BulkOperationsProvider, GlobalBulkOperationQueue } from '@/components/ats/BulkOperationQueue';
 *
 * // Wrap your app with the provider
 * <BulkOperationsProvider>
 *   <App />
 *   <GlobalBulkOperationQueue />
 * </BulkOperationsProvider>
 * ```
 */
import { useHistory } from 'react-router-dom';
import { useBreakpointValue } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useBulkOperationsOptional } from './BulkOperationsContext';
import { BulkOperationQueue } from './BulkOperationQueue';

interface GlobalBulkOperationQueueProps {
  /** Position from the left edge of the screen */
  leftOffset?: string;
  /** Position from the bottom edge of the screen */
  bottomOffset?: string;
  /** Width of the component */
  width?: string;
}

export const GlobalBulkOperationQueue = ({
  leftOffset = '80px',
  bottomOffset = '0',
  width = '416px',
}: GlobalBulkOperationQueueProps) => {
  const history = useHistory();
  const bulkOps = useBulkOperationsOptional();
  const RECENT_ACTIVITIES_PATH = '/notifications/recent-activities';
  const VIEW_MODE = {
    HIDDEN: 'hidden',
    MINIMIZED: 'minimized',
    EXPANDED: 'expanded',
  };

  // Track if queue should open expanded - set by bulkActionStarted event
  const [openExpanded, setOpenExpanded] = useState(false);
  // Counter to force remount when state changes
  const [mountKey, setMountKey] = useState(0);

  // Check if mobile view - don't render on mobile
  const isMobile = useBreakpointValue({ base: true, md: false });

  const isBulkActionsVisible = bulkOps?.isBulkActionsVisible ?? false;

  // Listen for bulk action started event
  useEffect(() => {
    const handleBulkActionStarted = () => {
      setOpenExpanded(true);
      setMountKey((k) => k + 1);
    };
    window.addEventListener('bulkActionStarted', handleBulkActionStarted);
    return () => {
      window.removeEventListener('bulkActionStarted', handleBulkActionStarted);
    };
  }, []);

  // When bulk actions become visible (queue will hide), prepare for minimized on next show
  useEffect(() => {
    if (isBulkActionsVisible) {
      queueMicrotask(() => {
        setOpenExpanded(false);
        setMountKey((k) => k + 1);
      });
    }
  }, [isBulkActionsVisible]);

  // Don't render when used outside BulkOperationsProvider
  if (!bulkOps) {
    return null;
  }

  const { config, viewMode, minimizeToSidebar, markActivitiesAsViewed } =
    bulkOps;

  // Don't render on mobile view
  if (isMobile) {
    return null;
  }

  // Don't render on recent activities page as it shows all activity information
  if (history.location.pathname === RECENT_ACTIVITIES_PATH) {
    return null;
  }

  // Don't render if no config or view mode is hidden/minimized
  if (!config) {
    return null;
  }

  if (viewMode === VIEW_MODE.HIDDEN || viewMode === VIEW_MODE.MINIMIZED) {
    return null;
  }

  // Mark activities as viewed (entity_views) then navigate to recent activities when expand is clicked
  const handleExpand = async () => {
    await markActivitiesAsViewed();
    history.push(RECENT_ACTIVITIES_PATH);
  };

  // When table bulk actions are visible, hide the queue
  if (isBulkActionsVisible) {
    return null;
  }

  return (
    <BulkOperationQueue
      key={mountKey}
      config={{
        ...config,
        // When a bulk operation just started, open expanded
        // Otherwise, open minimized
        defaultMinimized: !openExpanded,
        onClose: minimizeToSidebar,
        onExpand: handleExpand,
      }}
      leftOffset={leftOffset}
      bottomOffset={bottomOffset}
      width={width}
    />
  );
};
